/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import { useDispatch, useSelector } from "react-redux"; // Redux hooks
import { useTranslation } from "react-i18next"; // Translation hook
import { useEffect, useState } from "react";

// Importing Redux actions for creating, updating, deleting, and retrieving documents
import {
    CreateContractorDoc,
    CreateEmployeeDoc,
    DeleteDocumentById,
    GetContractorDocById,
    GetEmployeeDocById,
    UpdateContractorDoc,
    UpdateEmployeeDoc,
} from "../../../../../reduxToolkit/DocumentPanel/DocumentPanelApi";

import { ACTION_MODEL } from "../../../../../Helpers/ActionModel"; // Constants for action types
import { TABLES } from "../../../../../Apis/Tables"; // Table names used for API operations
import { getFileSizeFromBase64 } from "../../../../../Apis/functions"; // Utility function to calculate file size from Base64 string
import { toast } from "react-toastify";
import {getPhotoPicker, PHOTO_PICKER_MODEL} from '../../../../../components/PhotoPicker/SubComponent/Models/Models'; // Toast notifications
import { UploadImage } from "../../../../../reduxToolkit/ShareSlice/shareApi";

// Custom hook for managing the logic of the "Add Document" form
const AddDocumentApp = ({ activeStep, setModified, action, item, onHide }) => {
    const { t } = useTranslation(); // Hook for translations
    const dispatch = useDispatch(); // Redux dispatch function

    // Local state declarations
    const [disabled, setDisabled] = useState(false); // Whether the form is disabled (view-only mode)
    const [documentName, setDocumentName] = useState(''); // Name of the document
    const [departmentId, setDepartmentId] = useState(null); // Selected department
    const [isCompanyDoc, setIsCompanyDoc] = useState(false); // Whether the document is for the whole company
    const [instruction, setInstruction] = useState(''); // Any instructions related to the document
    const [emptyFields, setEmptyFields] = useState(false); // Flag for validation (e.g., missing required fields)
    const [dbFile, setDbFile] = useState(null); // Existing file name from the database
    const [photoPicker, setPhotoPicker] = useState({...PHOTO_PICKER_MODEL})  // File to be uploaded
    const [displayModal, setDisplayModal] = useState("")

    // Access department list from the global Redux state
    const { getAllDepartments } = useSelector((state) => state.documentPanelSlice);

    // Populate the form if editing or viewing an existing document
    useEffect(() => {
        if ((action === ACTION_MODEL.UPDATE || action === ACTION_MODEL.READ) && item) {
            // Dispatch the correct action based on active step
            const getDocAction =
            activeStep === 0
                ? GetEmployeeDocById(item.id)
                : GetContractorDocById(item.id);

            dispatch(getDocAction).then(({ payload: { data: { data } } }) => {
                // Populate form fields with data
                setDepartmentId(data.department?.id || null);
                setDocumentName(data.document);
                setInstruction(data.instruction || '');
                setIsCompanyDoc(data?.companyDocument);
                if (data.file) {
                    setPhotoPicker(prev => ({...prev, previewImage: { saved: true, name: data.path, size: getFileSizeFromBase64(data?.file) } }))
                    setDbFile(data.path);
                }

                setDisabled(action === ACTION_MODEL.READ); // Disable fields if read-only mode
            });
        }
    }, [action, item]);

    // Reset all local form states and close the modal
    const reset = () => {
        setPhotoPicker({...PHOTO_PICKER_MODEL});
        setDepartmentId(null);
        setDocumentName('');
        setInstruction('');
        setIsCompanyDoc(false);
        setEmptyFields(false);
        setDisabled(false);
        setDbFile(null);
        onHide(); // Close modal or form
    };

    // Create or update a document
    const handleCreateDocument = () => {
        // Check if the document name is empty
        if (!documentName) {
            setEmptyFields(true); // Set flag if there are empty required fields
            return; // Stop execution if the field is required
        }
        setEmptyFields(false); // Proceed if the document name is not empty

        // Prepare the data to be sent in the Redux action
        const data = {
            id: item?.id || null,
            document: documentName,
            departmentId,
            instruction: instruction || null,
            isCompanyDocument: isCompanyDoc,
        };

        // Dispatch the appropriate action based on the active step (0, 1, or 2)
        if (activeStep === 0) {
            dispatch(action === ACTION_MODEL.UPDATE ? UpdateEmployeeDoc(data) :CreateEmployeeDoc(data)).then(({payload:{data:{data}}}) => {
                hasFile(data?.id, TABLES.EMPLOYEE_DOCUMENT_COMPANY, TABLES.EMPLOYEE_DOCUMENT_COMPANY_FILE);
            }); // Step 0 is for employee
        }  else if (activeStep === 1) {
            dispatch(action === ACTION_MODEL.UPDATE ? UpdateContractorDoc(data) :CreateContractorDoc(data)).then(({payload:{data:{data}}}) => {
                hasFile(data?.id, TABLES.CONTRACTOR_DOCUMENT_COMPANY, TABLES.CONTRACTOR_DOCUMENT_COMPANY_FILE);
            }); // Step 1 is for contractor
        }
    };

    // Handle logic related to file presence and upload
    const hasFile = (id, option, deleteFileOption) => {
        let uploadFile = getPhotoPicker(photoPicker);
        if (action === ACTION_MODEL.UPDATE) {
            if (!uploadFile && dbFile) {
                // File was removed by the user
                dispatch(DeleteDocumentById({ id: id, option: deleteFileOption })).then(() => {
                    reset();
                    setModified(true);
                    toast.success(t("has_been_updated_successfully"));
                });
                return;
            } else if (uploadFile && typeof uploadFile?.saved === "undefined") {
                // New file uploaded
                handleUploadFile(id, option, uploadFile);
                return;
            }

            // No file changes
            reset();
            setModified(true);
            toast.success(t("has_been_updated_successfully"));
        } else {
            // Create flow
            if (uploadFile) {
                handleUploadFile(id, option, uploadFile);
                return;
            }
            reset();
            setModified(true);
            toast.success(t("has_been_created_successfully"));
        }
    }

    // Function to handle actual file upload via API
    const handleUploadFile = (id, option, uploadFile) => {
        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('id', id);
        formData.append('option', option);

        dispatch(UploadImage(formData)).then(() => {
            reset();
            setModified(true);
            toast.success(t("has_been_updated_successfully"));
        });
    };

    const handleDeletePhotoPicker = () => {
        setPhotoPicker({...PHOTO_PICKER_MODEL})
    }

    // Exposing relevant states and functions to the parent component
    return {
        t,
        activeStep,
        documentName,
        setDocumentName,
        departmentId,
        setDepartmentId,
        isCompanyDoc,
        setIsCompanyDoc,
        instruction,
        setInstruction,
        emptyFields,
        disabled,
        getAllDepartments,
        handleCreateDocument,
        reset,
        photoPicker,
        setPhotoPicker,
        handleDeletePhotoPicker,
        displayModal,
        setDisplayModal
    };
};

export default AddDocumentApp;
